import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { generateApiRoute } from '@klp/core/api/helpers';
import type { IFileObject } from '@klp/file/classes';
import { FileObject } from '@klp/file/classes';

import type { KLPSupportApiRequest } from './classes/KLPSupportApiRequest';
import type { IKLPSupportForm } from './classes/KLPSupportForm';
import { KLPSupportForm } from './classes/KLPSupportForm';
import type { KlpSupportResponse } from './classes/KLPSupportResponse';

interface SupportFormApiResponse {
    payload: IKLPSupportForm;
    attachments: Array<IFileObject>;
}

export const supportFormApiName = 'form';

export const supportFormApiRoutes = {
    form: () => generateApiRoute([supportFormApiName]),
    formById: (formId: string | number) =>
        generateApiRoute([supportFormApiName, formId]),
    formFiles: (formId: string | number) =>
        generateApiRoute([supportFormApiName, 'file', formId]),
};

export const supportApplicationFormApi = createApi({
    reducerPath: 'supportApplicationFormApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '/',
        prepareHeaders: (headers) => {
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['SupportForm'],
    endpoints: (builder) => ({
        getSupportForm: builder.query<KlpSupportResponse, string>({
            query: (id) => supportFormApiRoutes.formById(id),
            transformResponse: (response: SupportFormApiResponse) => {
                const klpSupportForm = KLPSupportForm.parseObject(
                    response.payload as IKLPSupportForm,
                );
                klpSupportForm.supportFormFiles = response.attachments.map(
                    (attachment: IFileObject) =>
                        FileObject.parseObject(attachment),
                );
                return { klpSupportForm };
            },
            providesTags: (result, error, id) => [{ type: 'SupportForm', id }],
        }),

        postSupportForm: builder.mutation<
            KlpSupportResponse,
            { klpSupportForm: KLPSupportForm }
        >({
            query: ({ klpSupportForm }) => {
                const { supportFormFiles, ...apiPayload } = klpSupportForm;

                const apiRequest: KLPSupportApiRequest = {
                    id: apiPayload.id,
                    originatorEmail: apiPayload.email,
                    formType: 'KLPSupportForm',
                    payload: { ...apiPayload },
                    attachments: Array.from(
                        Object.values(supportFormFiles).map((file) =>
                            FileObject.toBinaryFile(file),
                        ),
                    ),
                };

                return {
                    url: supportFormApiRoutes.form(),
                    method: 'POST',
                    body: apiRequest,
                };
            },
            transformResponse: (
                _response: unknown,
                _meta: unknown,
                arg: { klpSupportForm: KLPSupportForm },
            ) => ({
                klpSupportForm: arg.klpSupportForm,
            }),
            invalidatesTags: (result, error, { klpSupportForm }) => [
                { type: 'SupportForm', id: klpSupportForm.id },
            ],
        }),
    }),
});

export const { useGetSupportFormQuery, usePostSupportFormMutation } =
    supportApplicationFormApi;
